/**
 * Реестр продуктов платформы.
 *
 * Один сайт обслуживает несколько доменов, и продукт определяется по хосту
 * запроса, а не переменной сборки. Причина простая: `NEXT_PUBLIC_SITE_URL`
 * вшивается в бандл на этапе `next build`, поэтому одним образом нельзя
 * обслужить два домена — второй получил бы canonical и Open Graph первого.
 *
 * Пока datahunter.store и aisearch.tech не запущены, они показывают страницу
 * «скоро открытие». Когда продукт будет готов, достаточно снять здесь флаг
 * `comingSoon`.
 */

/** Описание продукта платформы. */
export interface Product {
  /** Короткий идентификатор, он же ключ переводов и имя проекта в аналитике. */
  id: string;
  /** Отображаемое название. */
  name: string;
  /** Домены, включая локальные для проверки без настоящего DNS. */
  hosts: string[];
  /** Пока true, вместо сайта показывается страница «скоро открытие». */
  comingSoon: boolean;
  /**
   * Ветка страниц продукта в дереве маршрутов.
   *
   * Домен продукта обслуживается своей веткой: middleware переписывает
   * `/app/json-editor` в `/<locale>/<routeSegment>/app/json-editor`. Адрес
   * в браузере при этом не меняется, сегмент наружу не виден.
   *
   * У основного сайта его нет: он живёт в корне дерева.
   */
  routeSegment?: string;
  /**
   * Суффикс переменных окружения с учётными данными OAuth2-клиента.
   *
   * У каждого продукта свой клиент в Authentik (Arhitect#56), поэтому креды
   * читаются как `AUTH_AUTHENTIK_CLIENT_ID_<envSuffix>`. Переменные без
   * суффикса остаются запасным вариантом: пока клиент продукта не заведён,
   * вход продолжает работать по-старому, а не падает.
   */
  envSuffix: string;
  /**
   * Идентификатор проекта в аналитике.
   *
   * Значение публичное: оно и так видно в исходниках страницы, секретом
   * является только CLIENT_SECRET для серверной отправки событий. Держим
   * его здесь, а не в переменной окружения, потому что домены обслуживает
   * один образ: переменная сборки была бы одна на все продукты сразу.
   *
   * Пустое значение выключает счётчик для продукта.
   */
  analyticsClientId?: string;
  /**
   * Идентификатор проекта аналитики для русского домена продукта.
   *
   * aisearch.ru — отдельная витрина со своей аудиторией, и считать её
   * вместе с .tech значит потерять обе цифры: рост одного домена
   * маскировал бы спад другого.
   */
  ruAnalyticsClientId?: string;
  /**
   * Номер счётчика Яндекс.Метрики.
   *
   * Рядом с OpenPanel, а не вместо него: тот отвечает на «сколько людей
   * и откуда», Метрика даёт Вебвизор, карту кликов и связку с
   * Вебмастером. По русскому поисковому трафику без второго работать
   * вслепую.
   *
   * Значение публичное по устройству счётчика: он и так виден в
   * исходниках страницы. Держим здесь, а не в переменной сборки, по той
   * же причине, что и остальные: домены обслуживает один образ, и
   * переменная была бы общей на все продукты сразу.
   *
   * Пустое значение выключает счётчик для продукта.
   */
  metrikaId?: string;
  /**
   * Ключ IndexNow: им подтверждается право подавать заявки на переобход
   * страниц в Bing, Yandex, Seznam и Naver.
   *
   * Значение публичное по самому устройству протокола: файл с этим же
   * ключом обязан лежать в корне сайта и открываться кому угодно, иначе
   * заявки молча отклоняются. Прятать в переменную окружения то, что мы
   * сами обязаны опубликовать, смысла нет: секретности это не добавляет,
   * а стоит ручной настройки на каждом домене, без которой ничего не
   * работает и никто об этом не узнаёт (#168).
   *
   * Ключ свой у каждого домена. Общий подтверждал бы владение всеми
   * тремя сайтами сразу: файл-то отдаётся одним и тем же образом.
   *
   * Пустое значение выключает IndexNow для продукта.
   */
  indexNowKey?: string;
  /**
   * Домены, у которых продукту принадлежат и все поддомены.
   *
   * Стенды выходят наружу как `2026.aisearch.ru`, и перечислять каждый
   * такой адрес в `hosts` значит однажды выкатить домен, на котором
   * вместо продукта откроется основной сайт: ровно это и случилось.
   *
   * Суффикс, а не «любой хост, содержащий имя»: `evilaisearch.ru`
   * совпадать не должен, иначе чужой сайт получал бы наши страницы.
   */
  hostSuffixes?: string[];
}

export const PRODUCTS: Product[] = [
  {
    id: 'ingdg',
    indexNowKey: '4a0dadf389a8351292fb8e1be09883ef',
    envSuffix: 'INGDG',
    name: 'INGDG',
    hosts: ['ingdg.com', 'www.ingdg.com', 'localhost', '127.0.0.1', 'ingdg.local'],
    comingSoon: false,
  },
  {
    id: 'datahunter',
    indexNowKey: 'd8e177c5db684fab4a27ca57d547c075',
    envSuffix: 'DATAHUNTER',
    name: 'DataHunter',
    hosts: [
      'datahunter.store',
      'www.datahunter.store',
      'dev-stand.datahunter.store',
      'datahunter.local',
      // Браузеры считают весь домен .localhost безопасным контекстом, а
      // разрешают его сами, без записи в hosts. Это нужно инструментам,
      // которым требуется Web Crypto: по обычному HTTP на постороннем
      // имени он недоступен, и локально их было бы не проверить.
      'datahunter.localhost',
    ],
    // Домен снова обслуживает сайт: витрина и бесплатные инструменты
    // (docs/products/datahunter-store/LAUNCH_TASKS.md §3.3).
    comingSoon: false,
    routeSegment: 'datahunter',
    // Счётчик нужен уже на заглушке: интерес к домену до запуска это
    // единственный доступный сигнал спроса.
    analyticsClientId: '596ea072-dace-4bc6-bf3a-cc50893de903',
  },
  {
    id: 'aisearch',
    indexNowKey: 'f3d58a2510a4a96a91f986cca4e19b75',
    envSuffix: 'AISEARCH',
    name: 'AiSearch',
    hosts: [
      'aisearch.tech',
      'www.aisearch.tech',
      'aisearch.ru',
      'www.aisearch.ru',
      'aisearch.local',
      // Браузеры считают весь домен .localhost безопасным контекстом и
      // разрешают его сами, без записи в hosts — см. комментарий у datahunter.
      'aisearch.localhost',
    ],
    comingSoon: false,
    routeSegment: 'aisearch',
    hostSuffixes: ['aisearch.ru'],
    analyticsClientId: '01bcc086-e64d-4b7d-a4a1-c17f4320d9bb',
    ruAnalyticsClientId: 'bd587141-c810-4fb3-9bcb-38cde33d7681',
    /*
      Тот же счётчик, что стоит на работающем aisearch.ru (проверено
      запросом к живому сайту). Это важнее, чем кажется: новый счётчик
      начал бы историю с нуля, и сравнить посещаемость до и после
      переезда было бы нечем — а именно это сравнение и есть приёмка
      всей задачи #181.

      Один на оба домена. Разделять, как у OpenPanel, здесь нечего:
      .tech по-русски не ранжируется, а вся история Метрики и связка с
      Вебмастером накоплены на русском домене.
    */
    metrikaId: '92301734',
  },
];

/** Продукт по умолчанию: неизвестный домен обслуживается как основной сайт. */
const DEFAULT_PRODUCT = PRODUCTS[0] as Product;

/**
 * Определяет продукт по заголовку Host.
 *
 * Порт отбрасывается: локально домены проверяются на нестандартных портах
 * (`datahunter.local:3031`), и без этого совпадения не было бы.
 */
export function productByHost(host: string | null | undefined): Product {
  if (!host) return DEFAULT_PRODUCT;

  const hostname = host.split(':')[0]?.toLowerCase() ?? '';
  return (
    PRODUCTS.find(
      (product) =>
        product.hosts.includes(hostname) ||
        product.hostSuffixes?.some((suffix) => hostname.endsWith(`.${suffix}`)),
    ) ?? DEFAULT_PRODUCT
  );
}
